// Helper calculations for Organization Report

// Validate numeric response (exclude N/A variants)
export function isValidNumericResponse(value) {
  return (
    value !== null &&
    value !== undefined &&
    value !== 'N/A' &&
    value !== 'n/a' &&
    value !== 'NA' &&
    value !== 'na' &&
    typeof value === 'number' &&
    !isNaN(value)
  );
}

// Apply reverse scoring if needed using questions structure
export function applyReverseIfNeeded(questionItem, structure) {
  try {
    const dim = structure?.dimensions?.find(d => d.id === (questionItem.id + 1));
    const fac = dim?.factors?.find(f => f.id === (questionItem.factor + 1));
    const qDef = fac?.questions?.[questionItem.q];
    if (qDef?.reverse) {
      if (typeof questionItem.response === 'number') {
        return 10 - questionItem.response;
      }
    }
  } catch (e) {
    // ignore lookup issues
  }
  return questionItem.response;
}

// Percentage distribution buckets (0-10 scale):
// - low: 0–4
// - neutral: 5–6
// - high: 7–10
// NOTE: Return keys preserved for compatibility with existing consumers
//   lt5_pct -> low (0–4)
//   between_5_7_pct -> neutral (5–6)
//   gte8_pct -> high (7–10)
export function distributionPct(values) {
  const nums = values.filter(v => typeof v === 'number' && !isNaN(v));
  const n = nums.length || 1;
  const lt5 = (nums.filter(v => v <= 4).length / n) * 100; // 0–4
  const mid = (nums.filter(v => v >= 5 && v <= 6).length / n) * 100; // 5–6
  const gte8 = (nums.filter(v => v >= 7).length / n) * 100; // 7–10
  return { lt5_pct: lt5, between_5_7_pct: mid, gte8_pct: gte8 };
}

// Pearson correlation on pairs [[x,y], ...]
export function pearsonCorrelation(pairs) {
  const n = pairs.length;
  if (n < 2) return 0;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, sumY2 = 0;
  for (const [x, y] of pairs) {
    sumX += x; sumY += y; sumXY += x * y; sumX2 += x * x; sumY2 += y * y;
  }
  const num = n * sumXY - sumX * sumY;
  const den = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  return den === 0 ? 0 : num / den;
}

// Get factor title from questions structure given [factorIndex, dimensionIndex]
// NOTE: Prefer index-based lookup. Some survey structures do not have 1-based,
// sequential `id` values for dimensions/factors (e.g. when factors are inserted),
// which would otherwise cause null titles even though the factor exists.
export function getFactorName(questionsStructure, factorTuple) {
  if (!factorTuple) return null;
  const [factorIndex, dimensionIndex] = factorTuple;
  const dimensions = questionsStructure?.dimensions || [];

  // 1) Index-based lookup (matches how the rest of ReportV2 iterates the structure)
  const factorByIndex = dimensions?.[dimensionIndex]?.factors?.[factorIndex];
  if (factorByIndex?.title) return factorByIndex.title;

  // 2) Legacy fallback: id-based lookup assuming 1-based sequential ids
  const dimensionById = dimensions.find(
    (dim) => Number(dim?.id) === Number(dimensionIndex) + 1
  );
  const factorById = dimensionById?.factors?.find(
    (fact) => Number(fact?.id) === Number(factorIndex) + 1
  );
  return factorById?.title ?? null;
}

// Build per-respondent averaged factor scores keyed by "factor_dimension"
export function buildRespondentFactorAverages(resp, questionsStructure) {
  const buckets = {};
  const items = resp?.questions || [];
  items.forEach(q => {
    if (q?.id == null || q?.factor == null) return;
    const val = applyReverseIfNeeded(q, questionsStructure);
    if (!isValidNumericResponse(val)) return;
    const key = `${q.factor}_${q.id}`;
    if (!buckets[key]) buckets[key] = { sum: 0, count: 0, factor: q.factor, dimension: q.id };
    buckets[key].sum += val; buckets[key].count += 1;
  });
  const result = {};
  Object.keys(buckets).forEach(k => {
    const b = buckets[k];
    result[k] = b.count ? (b.sum / b.count) : undefined;
  });
  return result;
}

// Build per-respondent averaged outcome scores by outcome section id
export function buildRespondentOutcomeAverages(resp, outcomeQuestions) {
  const results = {};
  const arr = resp?.employee_outcomes?.responses || [];
  const valid = arr.filter(i => {
    if (!(i?.clicked === true || i?.clicked === undefined)) return false;
    const val = i?.response;
    // Filter out null, undefined, 'N/A', '' and ensure it's a valid number
    return val !== null && 
           val !== undefined && 
           val !== 'N/A' && 
           val !== '' &&
           !isNaN(Number(val));
  });
  valid.forEach(i => {
    const sectionId = i.q; // align with existing outcome averaging logic
    if (!results[sectionId]) results[sectionId] = { sum: 0, count: 0 };
    results[sectionId].sum += Number(i.response); results[sectionId].count += 1;
  });
  const averages = {};
  Object.keys(results).forEach(id => {
    const b = results[id];
    averages[id] = b.count ? (b.sum / b.count) : undefined;
  });
  return averages;
}

// Compute correlations per outcome (section id) against each factor key
export function computeOutcomeFactorCorrelations(allResponses, outcomeQuestions, questionsStructure) {
  const pairIndex = {}; // outcomeId => factorKey => [[outcomeAvg, factorAvg], ...]
  allResponses.forEach(resp => {
    const factorAvg = buildRespondentFactorAverages(resp, questionsStructure);
    const outcomeAvg = buildRespondentOutcomeAverages(resp, outcomeQuestions);
    Object.keys(outcomeAvg).forEach(outcomeId => {
      Object.keys(factorAvg).forEach(fKey => {
        const x = outcomeAvg[outcomeId];
        const y = factorAvg[fKey];
        if (!isValidNumericResponse(x) || !isValidNumericResponse(y)) return;
        if (!pairIndex[outcomeId]) pairIndex[outcomeId] = {};
        if (!pairIndex[outcomeId][fKey]) pairIndex[outcomeId][fKey] = [];
        pairIndex[outcomeId][fKey].push([x, y]);
      });
    });
  });
  const result = {};
  Object.keys(pairIndex).forEach(outcomeId => {
    const m = pairIndex[outcomeId];
    result[outcomeId] = Object.keys(m).map(fKey => {
      const r = pearsonCorrelation(m[fKey]);
      const [f, d] = fKey.split('_').map(n => parseInt(n, 10));
      return { factor_id: fKey, factor_name: getFactorName(questionsStructure, [f, d]), r };
    }).sort((a, b) => b.r - a.r);
  });
  return result;
}

// Build outcome index: key `${q.q}-${q.s}` => { section, text }
export function buildOutcomeIndex(outcomeDef) {
  const questionIndex = {}; // key => {section, text}
  const sectionToQuestions = {}; // section => [keys]
  (outcomeDef?.questions || []).forEach(section => {
    const sectionName = section.name;
    (section.questions || []).forEach(q => {
      const key = `${q.q}-${q.s}`;
      questionIndex[key] = { section: sectionName, text: q.question };
      if (!sectionToQuestions[sectionName]) sectionToQuestions[sectionName] = [];
      sectionToQuestions[sectionName].push(key);
    });
  });
  return { questionIndex, sectionToQuestions };
}

// Compute outcome question-level averages and distributions
export function computeOutcomeQuestionStats(allResponses, outcomeDef) {
  const { questionIndex } = buildOutcomeIndex(outcomeDef);
  const perQuestionValues = {}; // key => [values]
  Object.keys(questionIndex).forEach(k => { perQuestionValues[k] = []; });
  allResponses.forEach(resp => {
    const arr = resp?.employee_outcomes?.responses || [];
    arr.forEach(i => {
      if (!(i?.clicked === true || i?.clicked === undefined)) return;
      const key = `${i.q}-${i.s}`;
      if (!questionIndex[key]) return;
      const val = i.response;
      // Filter out null, undefined, 'N/A', '' and ensure it's a valid number
      if (val !== null && 
          val !== undefined && 
          val !== 'N/A' && 
          val !== '' &&
          !isNaN(Number(val))) {
        perQuestionValues[key].push(Number(val));
      }
    });
  });
  const perQuestionStats = {}; // key => { average, distribution }
  Object.keys(perQuestionValues).forEach(k => {
    const vals = perQuestionValues[k];
    const avg = vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
    perQuestionStats[k] = { average: avg, distribution: distributionPct(vals) };
  });
  return { questionIndex, perQuestionStats };
}

// Collect outcome feedback text from `feedback_builder.responses` matching section id
export function collectOutcomeFeedbackText(allResponses, outcomeSectionId) {
  const texts = [];
  allResponses.forEach(resp => {
    const fb = resp?.feedback_builder?.responses || [];
    fb.forEach(f => {
      if (String(f.q) !== String(outcomeSectionId)) return;
      const t = f?.response || f?.feedback || '';
      if (t && typeof t === 'string') {
        texts.push(t);
      }
    });
  });
  return texts.join("\n");
}

// Factor question-level values for distributions and averages
export function gatherFactorQuestionValues(allResponses, dimIndex, factorIndex, localQIndex, structure) {
  const vals = [];
  allResponses?.forEach(resp => {
    resp?.questions?.forEach(q => {
      if (q?.id === dimIndex && q?.factor === factorIndex && q?.q === localQIndex) {
        const v = applyReverseIfNeeded(q, structure);
        if (isValidNumericResponse(v)) vals.push(v);
      }
    });
  });
  return vals;
}

// Factor-level values for distributions
export function gatherFactorValues(allResponses, dimIndex, factorIndex, structure) {
  const vals = [];
  allResponses?.forEach(resp => {
    resp?.questions?.forEach(q => {
      if (q?.id === dimIndex && q?.factor === factorIndex) {
        const v = applyReverseIfNeeded(q, structure);
        if (isValidNumericResponse(v)) vals.push(v);
      }
    });
  });
  return vals;
}


